/* Copyright (c) Trimble Inc. All rights reserved. */

import { Logger } from '@tcp-base/toolkit';
import { JWTHeader, JWTModel, JWTParseError, JWTPayload } from '../models/Token.model';
/**
 * JWT Parser Helper
 *
 * Utilities for parsing and extracting data from JWT tokens.
 * Handles base64 decoding of JWT payload and header sections.
 */

class JWTParseHelperImpl {
  constructor() {
    // constructor to prevent instantiation
  }
  /**
   * Parse a JWT token and return decoded header and payload
   *
   * @param token - The JWT token string
   * @returns Parsed JWT with header, payload, and signature
   * @throws Error if token format is invalid or decoding fails
   */
  public parseJWT = (token: string): JWTModel => {
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid token: Token must be a non-empty string');
    }

    // Split token into parts
    const parts = token.split('.');

    if (parts.length !== 3) {
      throw new Error('Invalid JWT format: Token must have exactly 3 parts separated by dots');
    }

    const [headerB64, payloadB64, signatureB64] = parts;

    try {
      // Decode header
      const headerJson = this.base64UrlDecode(headerB64);
      const header: JWTHeader = JSON.parse(headerJson);

      // Decode payload
      const payloadJson = this.base64UrlDecode(payloadB64);
      const payload: JWTPayload = JSON.parse(payloadJson);

      return {
        header,
        payload,
        signature: signatureB64,
        raw: {
          header: headerB64,
          payload: payloadB64,
          signature: signatureB64,
        },
      };
    } catch (error: unknown) {
      throw new Error(`Failed to parse JWT: ${(error as JWTParseError)?.message as string}}`);
    }
  };

  /**
   * Extract only the payload from a JWT token
   *
   * @param token - The JWT token string
   * @returns Decoded JWT payload
   */
  public getJWTPayload = (token: string): JWTPayload => {
    const parsed = this.parseJWT(token);
    return parsed.payload;
  };

  /**
   * Extract only the header from a JWT token
   *
   * @param token - The JWT token string
   * @returns Decoded JWT header
   */
  public getJWTHeader = (token: string): JWTHeader => {
    const parsed = this.parseJWT(token);
    return parsed.header;
  };

  /**
   * Check if a JWT token is expired
   *
   * @param token - The JWT token string
   * @param bufferSeconds - Optional buffer time in seconds (default: 0)
   * @returns True if token is expired, false otherwise
   */
  public isJWTExpired = (token: string, bufferSeconds = 0): boolean => {
    try {
      const payload = this.getJWTPayload(token);

      if (!payload.exp) {
        // No expiration claim means token doesn't expire
        return false;
      }

      const currentTimeSeconds = Math.floor(Date.now() / 1000);
      const expirationWithBuffer = payload.exp - bufferSeconds;

      return currentTimeSeconds >= expirationWithBuffer;
    } catch (error) {
      // If we can't parse the token, consider it expired
      Logger.warn('Failed to check JWT expiration:', error);
      return true;
    }
  };

  /**
   * Get expiration date from JWT token
   *
   * @param token - The JWT token string
   * @returns Date object representing expiration time, or null if no expiration
   */
  public getJWTExpiration = (token: string): Date | null => {
    try {
      const payload = this.getJWTPayload(token);

      if (!payload.exp) {
        return null;
      }

      return new Date(payload.exp * 1000);
    } catch (error) {
      Logger.warn('Failed to get JWT expiration:', error);
      return null;
    }
  };

  /**
   * Extract user information from JWT payload
   *
   * @param token - The JWT token string (typically ID token)
   * @returns User data extracted from token claims
   */
  public extractUserFromJWT = (
    token: string,
  ): {
    email?: string;
    given_name?: string;
    family_name?: string;
    name?: string;
    picture?: string;
    sub?: string;
    account_id?: string;
  } => {
    try {
      const payload = this.getJWTPayload(token);

      return {
        email: payload.email,
        given_name: payload.given_name,
        family_name: payload.family_name,
        name: payload.name,
        picture: payload.picture,
        sub: payload.sub,
        account_id: payload.account_id,
      };
    } catch (error) {
      Logger.warn('Failed to extract user from JWT:', error);
      return {};
    }
  };

  /**
   * Base64 URL decode (JWT uses base64url encoding, not standard base64)
   *
   * @param str - Base64 URL encoded string
   * @returns Decoded string
   */
  public base64UrlDecode = (str: string): string => {
    // Convert base64url to base64
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if necessary
    while (base64.length % 4) {
      base64 += '=';
    }

    try {
      // Decode base64
      const decoded = atob(base64);

      // Convert to UTF-8 (handle Unicode characters)
      return decodeURIComponent(
        decoded
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
    } catch (error) {
      throw new Error(`Failed to decode base64url: ${(error as JWTParseError)?.message as string}`);
    }
  };

  /**
   * Validate JWT token format (basic validation, not signature verification)
   *
   * @param token - The JWT token string
   * @returns True if format is valid, false otherwise
   */
  public isValidJWTFormat = (token: string): boolean => {
    try {
      this.parseJWT(token);
      return true;
    } catch (error) {
      Logger.warn('Failed to validate JWT format:', error);
      return false;
    }
  };
}

export const JWTParseHelper = new JWTParseHelperImpl();
export default JWTParseHelper;
